// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { RadioStyle } from "@insite/mobius/Radio";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { xBaseTheme } from "@tps/XDL/types";
import color from "@tps/XDL/Utilities/color";
import { css } from "styled-components";

const generalStyles = css`
    & + label {
        margin-left: 0;
        margin-right: 8px;
        align-items: flex-start;
    }
    & + label::before {
        flex: 0 0 13px;
        width: 13px;
        height: 13px;
        border-color: ${color("common.ui4")};
        margin-right: 8px;
        margin-top: 2px;
        background: ${color("common.ui2")};
    }
    &:checked + label::after {
        width: 13px;
        height: 13px;
        left: 0;
        top: 2px;
    }
    &:focus + label::before {
        box-shadow: none;
    }
`;

const hoverStyle = css`
    &:hover:not(:disabled) + label::before,
    &:hover:checked:not(:disabled) + label::before {
        border-color: ${color("common.ui8")};
    }
    &:hover:not(:disabled) + label {
        cursor: pointer;
    }
    &:hover:checked:not(:disabled) + label::after {
        border: 1px solid ${color("common.ui8")};
    }
`;

const checkedStyle = css`
    &:checked + label::before {
        border-color: ${color("common.ui4")};
        background: ${color("common.ui1")};
    }

    &:checked + label::after {
        background: ${color("common.ui9")};
        outline: 1px solid ${color("common.ui1")};
        outline-offset: -2px;
        border: 1px solid ${color("common.ui4")};
    }
`;

const disabledStyle = css`
    &:checked:disabled + label::before {
        border-color: ${color("common.ui4")};
        background: ${color("common.ui1")};
        opacity: 0.33;
    }

    &:checked:disabled + label::after {
        background: ${color("common.ui9")};
        outline: 1px solid ${color("common.ui1")};
        outline-offset: -2px;
        border: 1px solid ${color("common.ui4")};
        opacity: 0.33;
    }
    &:disabled + label:before {
        opacity: 0.33;
    }
`;

export default {
    defaultProps: {
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            size: 12,
            weight: 400,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
                user-select: none;
            `,
        },
        css: css`
            input {
                margin-left: 0px;
                margin-right: 0px;
                margin-top: 0;
                width: 13px;
                height: 13px;
                display: none;
                ${generalStyles}
                ${hoverStyle}
                ${checkedStyle}
                ${disabledStyle}
            }
        `,
    },
    groupDefaultProps: {
        mergeCss: true,
        labelProps: {
            size: 16,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                padding: 0;
                margin-bottom: 16px;
                &:empty {
                    display: none;
                }
            }
            ${(props: any) => {
                return props.horizontal
                    ? css`
                          display: inline-block;
                          & > ${RadioStyle} {
                              display: inline-block;
                              margin-top: 5px;
                              margin-right: 16px;
                          }
                      `
                    : css`
                          display: flex;
                          flex-direction: column;
                          ${RadioStyle} + ${RadioStyle} {
                              margin-top: 12px;
                          }
                      `;
            }}
        `,
    },
} as xBaseTheme["radio"];
